import { ScriptLoader } from '@thiememeulenhoff/js-sdk';
import { html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { TmElement } from '@thiememeulenhoff/lit-sdk';
import {
  Bluebillywig,
  BluebillywigPlayer,
  BluebillywigPlayerState,
  BluebillywigPlayerStateEnum,
} from './media-player.type';

declare global {
  interface Window {
    bluebillywig: Bluebillywig | null;
  }
}

export class MediaPlayerBaseComponent extends TmElement {
  @property({ type: String })
  override id!: string;

  @state()
  protected player!: BluebillywigPlayer;

  @state()
  currentPlayerState!: BluebillywigPlayerState;

  @state()
  initiated = false;

  @state()
  playing = false;

  @state()
  paused = false;

  @state()
  idle = false;

  @state()
  loading = false;

  @state()
  classes = {
    playing: this.playing,
    paused: this.paused,
    idle: this.idle,
  };

  @state()
  playerLoaded = false;

  @query('.player-container')
  playerContainer!: HTMLDivElement;

  constructor() {
    super();
    const src = 'https://thiememeulenhoff.bbvms.com/launchpad/';

    if (ScriptLoader.scriptExists(src)) {
      this.playerLoaded = true;
    }

    ScriptLoader.loadScript({
      src: 'https://thiememeulenhoff.bbvms.com/launchpad/',
      callback: () => {
        this.playerLoaded = true;
      },
    });

    this.#initPlayer();
  }

  override disconnectedCallback() {
    this.#destroyPlayer();
  }

  override render() {
    return html`
      ${!this.playerLoaded
        ? html`<a-loader>Aan het laden...</a-loader>`
        : html`<div class="player-container"></div>`}
    `;
  }

  protected togglePlayState() {
    if (!this.initiated) {
      throw Error(
        `Player is not initiated, probably because the Bluebillywig script failed to load.`,
      );
    }

    if (this.player.getState() === BluebillywigPlayerStateEnum.Playing) {
      this.player.pause('pause');
    } else {
      this.player.play('play');
    }
  }

  #initPlayer() {
    const loadInterval = setInterval(() => {
      if (window.bluebillywig) {
        this.#loadPlayer();
        this.initiated = true;
        this.player.on('play', () => this.#onPlay());
        this.player.on('pause', () => this.#onPause());
        this.player.on('statechange', () => this.#onStateChange());
        clearInterval(loadInterval);
      }
    }, 100);
  }

  #loadPlayer() {
    const url = `https://thiememeulenhoff.bbvms.com/p/basic_msp20_responsive_redesign/c/${this.id}.json`;
    const bbw = window.bluebillywig as Bluebillywig;

    this.player = new bbw.Player(url, {
      target: this.playerContainer,
      autoPlay: true,
    });
  }

  #destroyPlayer() {
    if (this.player && this.player.destruct) {
      this.player.destruct();
    }
  }

  #pauseOtherPlayers() {
    const bbw = window.bluebillywig as Bluebillywig;
    bbw.players.forEach((player: BluebillywigPlayer) => {
      if (player === this.player) return;
      player.pause('pause');
    });
  }

  #onStateChange() {
    this.currentPlayerState = this.player.getState();
    this.loading =
      this.currentPlayerState === BluebillywigPlayerStateEnum.Loading;
    this.playing =
      this.currentPlayerState === BluebillywigPlayerStateEnum.Playing;
    this.paused =
      this.currentPlayerState === BluebillywigPlayerStateEnum.Paused;
    this.idle = this.currentPlayerState === BluebillywigPlayerStateEnum.Idle;

    const event = new Event(this.currentPlayerState);
    this.dispatchLocalEvent({
      eventName: this.currentPlayerState,
      detail: event,
    });
  }

  #onPlay() {
    this.#pauseOtherPlayers();
  }

  #onPause(): BluebillywigPlayerState {
    // Just returning the state for now
    return this.player.getState();
  }
}
